/**
 * Ground mesh, displaced from the same analytic height function the server and
 * the player controller use. Nothing about the terrain is transmitted — it is
 * rebuilt from the seed.
 */

import * as THREE from 'three';
import { WORLD, type World } from '@static-pines/shared';

export function buildTerrain(world: World): THREE.Mesh {
  const size = WORLD.SIZE;
  const segments = WORLD.TERRAIN_SEGMENTS;

  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
  geometry.rotateX(-Math.PI / 2);

  const position = geometry.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(position.count * 3);

  // Albedo values — see the note in Forest.ts about picking these too dark.
  const lowland = new THREE.Color(0x44562f);
  const dirt = new THREE.Color(0x5d4d36);
  const rock = new THREE.Color(0x6e716b);
  const scratch = new THREE.Color();

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const z = position.getZ(i);
    const y = world.heightAt(x, z);
    position.setY(i, y);

    // Tint by slope so ridges read as rock and hollows as damp earth. Cheap
    // variation that survives being seen almost entirely in the dark.
    const slope = world.slopeAt(x, z);
    scratch.copy(lowland).lerp(dirt, Math.min(1, slope * 1.6));
    scratch.lerp(rock, Math.min(1, Math.max(0, (slope - 0.45) * 2.2)));

    const jitter = 0.88 + ((Math.sin(x * 12.9898 + z * 78.233) * 43758.5453) % 1) * 0.24;
    colors[i * 3] = scratch.r * jitter;
    colors[i * 3 + 1] = scratch.g * jitter;
    colors[i * 3 + 2] = scratch.b * jitter;
  }

  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.computeVertexNormals();

  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    fog: true,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  mesh.name = 'terrain';
  return mesh;
}
